import { useCallback, useState } from 'react';
import { toast } from 'sonner';

import {
  DEFAULT_FAX_TITLE,
  documentRecipientFromMarkdown,
  FAX_DRAFT_STORE,
  formatFaxNumberInFaxDocument,
  useDocumentPdfPreview,
  type DocumentDraftSeed,
} from '@/entities/document-draft';
import {
  DocumentDraftEditor,
  useDocumentAttachments,
  useDocumentDraft,
} from '@/features/document-draft-editing';
import { FaxEditorSendDialog, useFaxSend } from '@/features/fax-send';
import { isValidNanpPhone } from '@/shared/lib/formatters';
import { closeFaxEditor } from '@/shared/stores';
import { FaxNumberFormattingPlugin } from '@/shared/ui/primitives/lexical-markdown-editor';

interface FaxEditorWorkspaceProps {
  patientId: string;
  draftId?: string;
  seed?: DocumentDraftSeed;
  onClose: () => void;
}

export function FaxEditorWorkspace({ patientId, draftId, seed, onClose }: FaxEditorWorkspaceProps) {
  const [showPreview, setShowPreview] = useState(draftId !== undefined);
  const [faxValidationVisible, setFaxValidationVisible] = useState(false);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const close = useCallback(() => {
    closeFaxEditor();
    onClose();
  }, [onClose]);
  const editor = useDocumentDraft({ patientId, draftId, seed, store: FAX_DRAFT_STORE });
  const attachments = useDocumentAttachments(patientId, editor.draft, editor.updateDraft);
  const preview = useDocumentPdfPreview(patientId, editor.draft, {
    mode: showPreview && editor.loadState === 'ready' ? 'visible' : 'hidden',
    refresh: draftId !== undefined ? 'always' : 'cached',
  });
  const sender = useFaxSend(patientId, editor.draft, close);

  const openSendDialog = useCallback(() => {
    if (editor.draft === null) {
      return;
    }
    const recipient = documentRecipientFromMarkdown(editor.draft.content.bodyMarkdown);
    if (!isValidNanpPhone(recipient.faxNumber)) {
      setFaxValidationVisible(true);
      return;
    }
    if (editor.draft.content.bodyMarkdown.trim() === '') {
      toast.info('Fax body is required.', { description: 'Add content before sending the fax.' });
      return;
    }
    setFaxValidationVisible(false);
    setSendDialogOpen(true);
  }, [editor.draft]);

  return (
    <DocumentDraftEditor
      documentLabel="fax"
      defaultTitle={DEFAULT_FAX_TITLE}
      placeholder="Write the fax…"
      editorPlugins={<FaxNumberFormattingPlugin />}
      formatMarkdown={formatFaxNumberInFaxDocument}
      backLabel="Faxes"
      onBack={close}
      onSend={openSendDialog}
      sendDisabled={sender.sending}
      editor={editor}
      attachments={attachments}
      previewUrl={preview.previewUrl}
      previewState={preview.previewState}
      showPreview={showPreview}
      onShowPreviewChange={setShowPreview}
      alert={
        faxValidationVisible ? 'Enter a valid fax number in the Fax line before sending.' : null
      }
      renderDialogs={(draft) => (
        <FaxEditorSendDialog
          draft={draft}
          open={sendDialogOpen}
          sending={sender.sending}
          onOpenChange={setSendDialogOpen}
          onSend={() => void sender.send()}
        />
      )}
    />
  );
}
